import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Users, AlertTriangle, Clock, CheckCircle2, Inbox, FilterX, ShieldAlert, ListTodo, Flame, Building2, ShieldCheck, CalendarDays } from "lucide-react";
import {
  listClients,
  listDeadlines,
  updateDeadline,
  getSummary,
  getCertificateAlerts,
  listTasks,
  getToday,
  statusRowClass,
  daysUntil,
  formatBRDate,
  TAX_TYPES,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const KpiCard = ({ icon: Icon, label, value, accent, testId }) => (
  <div
    data-testid={testId}
    className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 transition-all duration-200 hover:shadow-sm hover:-translate-y-[1px]"
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">
          {label}
        </p>
        <p className="font-display text-3xl font-semibold text-[#0F172A] mt-2">
          {value}
        </p>
      </div>
      <div
        className={`h-10 w-10 rounded-lg flex items-center justify-center ${accent}`}
      >
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </div>
);

const StatusBadge = ({ deadline }) => {
  if (deadline.status === "completed") {
    return (
      <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-0">
        Concluído
      </Badge>
    );
  }
  const d = daysUntil(deadline.due_date);
  if (d < 0)
    return (
      <Badge className="bg-red-100 text-red-700 hover:bg-red-100 border-0">
        Vencido ({Math.abs(d)}d)
      </Badge>
    );
  if (d === 0)
    return (
      <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 border-0">
        Vence hoje
      </Badge>
    );
  if (d <= 45)
    return (
      <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 border-0">
        Em {d}d
      </Badge>
    );
  return (
    <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 border-0">
      Em {d}d
    </Badge>
  );
};

export default function Dashboard() {
  const { isSuperAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [summary, setSummary] = useState(null);
  const [certAlerts, setCertAlerts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [today, setToday] = useState({ deadlines: [], tasks: [] });
  const [filter, setFilter] = useState("all"); // all | pending | completed
  const [clientFilter, setClientFilter] = useState("all");
  const [taxFilter, setTaxFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    try {
      if (isSuperAdmin) {
        const s = await getSummary();
        setSummary(s);
      } else {
        const [c, d, s, ca, t, td] = await Promise.all([
          listClients(),
          listDeadlines(),
          getSummary(),
          getCertificateAlerts(),
          listTasks({ status: "pending" }).catch(() => []),
          getToday().catch(() => ({ deadlines: [], tasks: [] })),
        ]);
        setClients(c);
        setDeadlines(d);
        setSummary(s);
        setCertAlerts(ca);
        setTasks(t);
        setToday(td);
      }
    } catch (e) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line
  }, [isSuperAdmin]);

  // NOTE: All hooks (useMemo etc.) MUST be called before any conditional early return
  // to satisfy React's rules-of-hooks. These memos are only used in the tenant branch.
  const clientMap = useMemo(
    () => Object.fromEntries(clients.map((c) => [c.id, c])),
    [clients]
  );

  const filtered = useMemo(() => {
    return deadlines.filter((d) => {
      if (filter !== "all" && d.status !== filter) return false;
      if (clientFilter !== "all" && d.client_id !== clientFilter) return false;
      if (taxFilter !== "all" && d.tax_type !== taxFilter) return false;
      return true;
    });
  }, [deadlines, filter, clientFilter, taxFilter]);

  // ============================================================
  // Super-admin: renderiza apenas visão de plataforma
  // ============================================================
  if (isSuperAdmin) {
    return (
      <div className="space-y-6" data-testid="platform-dashboard">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A]">
            Painel da Plataforma
          </h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1">
            Visão agregada dos escritórios cadastrados. Dados internos de cada escritório são privados.
          </p>
        </div>
        {loading || !summary ? (
          <div className="p-12 text-center text-slate-400 text-sm">Carregando...</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KpiCard
                icon={Building2}
                label="Escritórios ativos"
                value={summary.active_orgs ?? 0}
                accent="bg-emerald-100 text-emerald-700"
                testId="kpi-active-orgs"
              />
              <KpiCard
                icon={Building2}
                label="Escritórios inativos"
                value={summary.inactive_orgs ?? 0}
                accent="bg-slate-100 text-slate-600"
                testId="kpi-inactive-orgs"
              />
              <KpiCard
                icon={Users}
                label="Usuários totais"
                value={summary.total_users ?? 0}
                accent="bg-blue-100 text-blue-700"
                testId="kpi-total-users"
              />
              <KpiCard
                icon={ShieldCheck}
                label="Super-admins"
                value={summary.total_super_admins ?? 0}
                accent="bg-amber-100 text-amber-700"
                testId="kpi-super-admins"
              />
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h2 className="font-display text-lg font-semibold text-[#0F172A]">
                Próximos passos
              </h2>
              <p className="text-sm text-slate-500 mt-2">
                Como super-admin da plataforma, você só tem acesso à gestão de escritórios e outros
                super-admins. Os dados internos (clientes, prazos, tarefas, fichas) de cada escritório
                são privados e acessíveis apenas pelos usuários daquele escritório.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to="/escritorios">
                  <Button className="bg-[#0F2942] hover:bg-[#173E63] text-white" data-testid="cta-manage-orgs">
                    <Building2 className="h-4 w-4 mr-2" />
                    Gerenciar Escritórios
                  </Button>
                </Link>
                <Link to="/usuarios">
                  <Button variant="outline" data-testid="cta-manage-super-admins">
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    Gerenciar Super-admins
                  </Button>
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  const clearFilters = () => {
    setFilter("all");
    setClientFilter("all");
    setTaxFilter("all");
  };

  const hasActiveFilters =
    filter !== "all" || clientFilter !== "all" || taxFilter !== "all";

  const toggleStatus = async (deadline) => {
    const next = deadline.status === "completed" ? "pending" : "completed";
    try {
      await updateDeadline(deadline.id, { status: next });
      toast.success(next === "completed" ? "Marcado como concluído" : "Marcado como pendente");
      loadAll();
    } catch {
      toast.error("Não foi possível atualizar");
    }
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A]">
          Dashboard
        </h1>
        <p className="text-sm sm:text-base text-slate-500 mt-1">
          Acompanhe os próximos vencimentos e o status de cada prazo.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <KpiCard
          testId="kpi-clients"
          icon={Users}
          label="Clientes"
          value={summary?.total_clients ?? "—"}
          accent="bg-slate-100 text-[#0F2942]"
        />
        <KpiCard
          testId="kpi-pending"
          icon={Clock}
          label="Prazos Pendentes"
          value={summary?.pending ?? "—"}
          accent="bg-blue-50 text-[#0F2942]"
        />
        <KpiCard
          testId="kpi-overdue"
          icon={AlertTriangle}
          label="Prazos Vencidos"
          value={summary?.overdue ?? "—"}
          accent="bg-red-50 text-red-600"
        />
        <KpiCard
          testId="kpi-completed"
          icon={CheckCircle2}
          label="Concluídos"
          value={summary?.completed ?? "—"}
          accent="bg-green-50 text-green-600"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <KpiCard
          testId="kpi-tasks-pending"
          icon={ListTodo}
          label="Tarefas Pendentes"
          value={summary?.tasks_pending ?? "—"}
          accent="bg-blue-50 text-[#0F2942]"
        />
        <KpiCard
          testId="kpi-tasks-completed"
          icon={CheckCircle2}
          label="Tarefas Concluídas"
          value={summary?.tasks_completed ?? "—"}
          accent="bg-green-50 text-green-600"
        />
        <KpiCard
          testId="kpi-tasks-overdue"
          icon={Flame}
          label="Tarefas Atrasadas"
          value={summary?.tasks_overdue ?? "—"}
          accent="bg-red-50 text-red-600"
        />
      </div>

      {(today.deadlines.length > 0 || today.tasks.length > 0) && (
        <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden" data-testid="today-panel">
          <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-[#0F2942] dark:text-blue-400" />
              <h2 className="font-display text-lg font-semibold text-[#0F172A] dark:text-white">Hoje</h2>
              <span className="text-xs text-slate-400 dark:text-slate-500">
                {today.deadlines.length} prazo{today.deadlines.length !== 1 ? "s" : ""} · {today.tasks.length} tarefa{today.tasks.length !== 1 ? "s" : ""}
              </span>
            </div>
          </div>
          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100 dark:divide-slate-800">
            <div className="p-4" data-testid="today-deadlines-col">
              <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-2">
                Prazos vencendo hoje
              </p>
              {today.deadlines.length === 0 ? (
                <p className="text-sm text-slate-400 dark:text-slate-500">Nenhum prazo hoje.</p>
              ) : (
                <ul className="space-y-1.5">
                  {today.deadlines.slice(0, 6).map((d) => (
                    <li key={d.id} data-testid={`today-deadline-${d.id}`} className="text-sm flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                      <span className="font-medium truncate">{d.tax_type}</span>
                      <span className="text-slate-500 dark:text-slate-400 text-xs truncate">— {d.client_name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="p-4" data-testid="today-tasks-col">
              <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-2">
                Tarefas para hoje
              </p>
              {today.tasks.length === 0 ? (
                <p className="text-sm text-slate-400 dark:text-slate-500">Nenhuma tarefa hoje.</p>
              ) : (
                <ul className="space-y-1.5">
                  {today.tasks.slice(0, 6).map((t) => (
                    <li key={t.id} data-testid={`today-task-${t.id}`} className="text-sm flex items-center gap-2">
                      <ListTodo className={`h-3.5 w-3.5 flex-shrink-0 ${t.priority === "high" ? "text-red-500" : t.priority === "medium" ? "text-yellow-500" : "text-slate-400"}`} />
                      <span className="font-medium truncate">{t.title}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      )}

      {tasks.length > 0 && (
        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="upcoming-tasks">
          <div className="flex items-center justify-between p-5 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <ListTodo className="h-5 w-5 text-[#0F2942]" />
              <h2 className="font-display text-lg font-semibold text-[#0F172A]">Próximas Tarefas</h2>
            </div>
            <Link to="/tarefas" className="text-xs font-medium text-[#0F2942] hover:underline">
              Ver todas →
            </Link>
          </div>
          <ul className="divide-y divide-slate-100">
            {tasks.slice(0, 5).map((t) => {
              const overdue = t.due_date && t.due_date < new Date().toISOString().slice(0, 10);
              const priorityColor = t.priority === "high" ? "text-red-600" : t.priority === "medium" ? "text-yellow-600" : "text-slate-400";
              return (
                <li key={t.id} className={`flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors ${overdue ? "border-l-4 border-l-red-400" : ""}`}
                    data-testid={`upcoming-task-${t.id}`}>
                  <Flame className={`h-4 w-4 flex-shrink-0 ${priorityColor}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#0F172A] text-sm truncate">{t.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {t.assignee_name || "—"}
                      {t.due_date && (
                        <span className={overdue ? "text-red-600 font-medium ml-2" : "ml-2"}>
                          • Prazo: {formatBRDate(t.due_date)}
                        </span>
                      )}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {certAlerts.length > 0 && (
        <section
          className="bg-white border border-slate-200 rounded-xl overflow-hidden"
          data-testid="cert-alerts-section"
        >
          <div className="flex items-center gap-2 p-5 border-b border-slate-200">
            <ShieldAlert className="h-5 w-5 text-red-600" />
            <div>
              <h2 className="font-display text-lg font-semibold text-[#0F172A]">
                Certificados Digitais — Alertas
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Vermelho: 10 dias ou menos • Amarelo: até 30 dias
              </p>
            </div>
          </div>
          <ul className="divide-y divide-slate-100">
            {certAlerts.map((a) => {
              const critical = a.severity === "critical";
              return (
                <li
                  key={a.client_id}
                  className={`flex items-center justify-between p-4 ${
                    critical ? "bg-red-50" : "bg-yellow-50"
                  }`}
                  data-testid={`cert-alert-${a.client_id}`}
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle
                      className={`h-5 w-5 mt-0.5 ${
                        critical ? "text-red-600" : "text-yellow-600"
                      }`}
                    />
                    <div>
                      <p className="font-medium text-[#0F172A]">{a.name}</p>
                      <p className="text-xs text-slate-600 mt-0.5">
                        Certificado {a.tipo_certificado || "—"} • vence em{" "}
                        {formatBRDate(a.validade_certificado)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge
                      className={
                        critical
                          ? "bg-red-600 text-white hover:bg-red-600 border-0"
                          : "bg-yellow-500 text-white hover:bg-yellow-500 border-0"
                      }
                    >
                      {a.days_until < 0
                        ? `Vencido há ${Math.abs(a.days_until)}d`
                        : a.days_until === 0
                        ? "Vence hoje"
                        : `${a.days_until}d`}
                    </Badge>
                    <Button asChild size="sm" variant="outline">
                      <Link to={`/clientes/${a.client_id}`}>Abrir ficha</Link>
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section
        className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5"
        data-testid="dashboard-filters"
      >
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="flex-1">
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 block mb-1.5">
              Filtrar por Cliente
            </label>
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger data-testid="filter-client-select">
                <SelectValue placeholder="Todos os clientes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os clientes</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 block mb-1.5">
              Filtrar por Tipo de Licença
            </label>
            <Select value={taxFilter} onValueChange={setTaxFilter}>
              <SelectTrigger data-testid="filter-tax-select">
                <SelectValue placeholder="Todos os tipos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                {TAX_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Button
              variant="outline"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              data-testid="clear-filters-button"
              className="w-full lg:w-auto"
            >
              <FilterX className="h-4 w-4 mr-2" />
              Limpar Filtros
            </Button>
          </div>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-5 sm:p-6 border-b border-slate-200">
          <div>
            <h2 className="font-display text-xl font-semibold text-[#0F172A]">
              Próximos Vencimentos
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Amarelo: vence em até 45 dias • Vermelho: vencido
            </p>
          </div>
          <div className="flex items-center gap-2">
            {[
              { v: "all", label: "Todos" },
              { v: "pending", label: "Pendentes" },
              { v: "completed", label: "Concluídos" },
            ].map((f) => (
              <button
                key={f.v}
                onClick={() => setFilter(f.v)}
                data-testid={`filter-${f.v}`}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  filter === f.v
                    ? "bg-[#0F2942] text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-400 text-sm">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="empty-deadlines">
            <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">
              {deadlines.length === 0
                ? "Nenhum prazo cadastrado ainda. Vá em Prazos para adicionar."
                : "Nenhum prazo corresponde ao filtro selecionado."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="deadlines-table">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left font-semibold px-4 py-3">Cliente</th>
                  <th className="text-left font-semibold px-4 py-3">Tributo</th>
                  <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">
                    Descrição
                  </th>
                  <th className="text-left font-semibold px-4 py-3">Vencimento</th>
                  <th className="text-left font-semibold px-4 py-3">Status</th>
                  <th className="text-right font-semibold px-4 py-3">Ação</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => (
                  <tr
                    key={d.id}
                    className={`${statusRowClass(d)} border-b border-slate-100 last:border-0`}
                    data-testid={`deadline-row-${d.id}`}
                  >
                    <td className="px-4 py-3 font-medium text-[#0F172A]">
                      {clientMap[d.client_id]?.name || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{d.tax_type}</td>
                    <td className="px-4 py-3 text-slate-500 hidden md:table-cell">
                      {d.description || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                      {formatBRDate(d.due_date)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge deadline={d} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant={d.status === "completed" ? "outline" : "default"}
                        onClick={() => toggleStatus(d)}
                        data-testid={`toggle-status-${d.id}`}
                        className={
                          d.status === "completed"
                            ? "border-slate-300 text-slate-700 hover:bg-slate-100"
                            : "bg-[#0F2942] hover:bg-[#173E63] text-white"
                        }
                      >
                        {d.status === "completed" ? "Reabrir" : "Concluir"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
